
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  X, 
  Star, 
  User, 
  Award,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Calendar,
  Briefcase
} from 'lucide-react';
import { cn } from "@/lib/utils";

interface Interview {
  id: string;
  candidateId: string;
  candidateName: string;
  firstName: string;
  lastName: string;
  candidateEmail: string;
  position: string;
  jobDescription?: string;
  type: string;
  status: string;
  overallRating: number;
  recommendation: string;
  skills: {
    technical: number;
    communication: number;
    problemSolving: number;
    teamwork: number;
    leadership: number;
    adaptability?: number;
    creativity?: number;
    timeManagement?: number;
    [key: string]: number | undefined;
  };
  notes: string;
  strengths?: string;
  weaknesses?: string;
  createdAt: string;
  updatedAt: string;
}

interface InterviewResultsProps {
  interview: Interview;
  onClose: () => void;
}

export function InterviewResults({ interview, onClose }: InterviewResultsProps) {
  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'hire': return 'bg-green-50 text-green-700 border-green-200';
      case 'strong_hire': return 'bg-green-100 text-green-800 border-green-300';
      case 'maybe': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'no_hire': return 'bg-red-50 text-red-700 border-red-200';
      case 'no': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getSkillColor = (score: number) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    if (score >= 4) return 'text-orange-600';
    return 'text-red-600';
  };

  const coreSkills = [
    { key: 'technical', label: 'Technical Skills' },
    { key: 'communication', label: 'Communication' },
    { key: 'problemSolving', label: 'Problem Solving' },
    { key: 'teamwork', label: 'Teamwork' },
    { key: 'leadership', label: 'Leadership' }
  ];

  const additionalSkills = [
    { key: 'adaptability', label: 'Adaptability' },
    { key: 'creativity', label: 'Creativity' },
    { key: 'timeManagement', label: 'Time Management' }
  ].filter(skill => interview.skills[skill.key] !== undefined);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden border border-slate-200">
        {/* Header */}
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center flex-shrink-0">
                <User className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-2xl font-bold text-slate-900">Interview Results</h2>
                <p className="text-slate-600 mt-1 truncate">{interview.candidateName} - {interview.position}</p>
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-slate-500" />
                    <span className="text-sm text-slate-500">
                      {new Date(interview.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Briefcase className="h-4 w-4 text-slate-500" />
                    <span className="text-sm text-slate-500 capitalize">{interview.type} Interview</span>
                  </div>
                </div>
              </div>
            </div>
            <Button
              onClick={onClose}
              variant="outline"
              size="sm"
              className="rounded-lg border-slate-200 hover:bg-slate-100 flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(92vh-140px)] p-6">
          <div className="space-y-6">
            {/* Overall Results */}
            <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                  <Award className="h-5 w-5 mr-2 text-slate-600" />
                  Final Assessment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="text-center p-6 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Star className="h-6 w-6 text-yellow-500 fill-current" />
                      <span className="text-3xl font-bold text-slate-900">{interview.overallRating}/10</span>
                    </div>
                    <p className="text-sm text-slate-600">Overall Rating</p>
                  </div>
                  <div className="text-center p-6 bg-slate-50 rounded-lg">
                    <Badge className={cn("text-lg font-medium border px-4 py-2", getRecommendationColor(interview.recommendation))}>
                      {interview.recommendation.replace('_', ' ').toUpperCase()}
                    </Badge>
                    <p className="text-sm text-slate-600 mt-2">Final Recommendation</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Skills Breakdown */}
            <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                  <Star className="h-5 w-5 mr-2 text-slate-600" />
                  Skills Assessment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Core Skills */}
                  <div>
                    <h4 className="font-medium text-slate-900 mb-4">Core Skills</h4>
                    <div className="space-y-3">
                      {coreSkills.map((skill) => {
                        const skillValue = interview.skills[skill.key] || 0;
                        return (
                          <div key={skill.key} className="flex items-center justify-between">
                            <span className="text-sm text-slate-700">{skill.label}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 bg-slate-200 rounded-full h-2">
                                <div 
                                  className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-green-500"
                                  style={{ width: `${(skillValue / 10) * 100}%` }}
                                />
                              </div>
                              <span className={cn("font-bold text-sm w-8 text-right", getSkillColor(skillValue))}>
                                {skillValue}/10
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Additional Skills */}
                  <div>
                    <h4 className="font-medium text-slate-900 mb-4">Additional Skills</h4>
                    <div className="space-y-3">
                      {additionalSkills.map((skill) => {
                        const skillValue = interview.skills[skill.key] || 0;
                        return (
                          <div key={skill.key} className="flex items-center justify-between">
                            <span className="text-sm text-slate-700">{skill.label}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 bg-slate-200 rounded-full h-2">
                                <div 
                                  className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-green-500"
                                  style={{ width: `${(skillValue / 10) * 100}%` }}
                                />
                              </div>
                              <span className={cn("font-bold text-sm w-8 text-right", getSkillColor(skillValue))}>
                                {skillValue}/10
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Feedback */}
            {(interview.strengths || interview.weaknesses || interview.notes) && (
              <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                    <MessageSquare className="h-5 w-5 mr-2 text-slate-600" />
                    Interview Feedback
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {interview.strengths && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <ThumbsUp className="h-4 w-4 text-green-600" />
                        <h4 className="font-medium text-green-700">Key Strengths</h4>
                      </div>
                      <p className="text-sm text-slate-700 bg-green-50 p-3 rounded-lg border border-green-200">
                        {interview.strengths}
                      </p>
                    </div>
                  )}
                  
                  {interview.weaknesses && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <ThumbsDown className="h-4 w-4 text-orange-600" />
                        <h4 className="font-medium text-orange-700">Areas for Improvement</h4>
                      </div>
                      <p className="text-sm text-slate-700 bg-orange-50 p-3 rounded-lg border border-orange-200">
                        {interview.weaknesses}
                      </p>
                    </div>
                  )}
                  
                  {interview.notes && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <MessageSquare className="h-4 w-4 text-slate-600" />
                        <h4 className="font-medium text-slate-700">Interview Notes</h4>
                      </div>
                      <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg border border-slate-200">
                        {interview.notes}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-4 bg-slate-50">
          <div className="flex items-center justify-end">
            <Button
              onClick={onClose}
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
} 